import z from "zod";
import { getHeaders, PURCHASER_API_BASE_URL } from "../Constants";
import type { RFQStatus } from "./RFQ";

/**
 *
 * These types should be kept in sync with the backend at:
 * src/RFQv2/spend/frontend-types.ts
 *
 */

export type SpendMoney = {
    amount: number;
    formatted: string;
};

export type SpendDashboardItem = {
    id: number;
    itemName: string;
    quantity: number;
    categoryId: number | null;
    categoryName: string | null;
    categoryColorHex: string | null;
    rfqUniquePublicId: string;
    rfqStatus: RFQStatus;
    dateSourcedAt: string | null;
    quoteCount: number;
    quotedVendorCount: number;
    awardedVendorCount: number;
    awardedVendorName: string | null;
    awardedVendorId: number | null;
    awardedUnitPrice: SpendMoney | null;
    awardedTotalPrice: SpendMoney | null;
    lowestQuotedUnitPrice: SpendMoney | null;
    highestQuotedUnitPrice: SpendMoney | null;
    potentialSavings: SpendMoney | null;
};

export type SpendDashboardCategory = {
    id: number;
    name: string;
    colorHex: string;
    totalSpend: SpendMoney;
    totalSpendPercentage: number;
    itemCount: number;
    vendorCount: number;
    lastAwardedVendorName: string | null;
    lastAwardedVendorId: number | null;
    lastRfqItemName: string | null;
    lastSourcedAt: string | null;
};

export type SpendCategoryListing = {
    id: number;
    name: string;
    colorHex: string;
    itemCount: number;
    createdAt: string;
    lastSourcedAt: string | null;
};

export type SpendDashboard = {
    summary: {
        totalRfqsSent: number;
        totalItemsRequested: number;
        avgRfqSpend: SpendMoney | null;
        totalSpend: SpendMoney;
    };
    chart: {
        totalSpend: SpendMoney;
        categories: SpendDashboardCategory[];
    };
    frequentItems: {
        itemName: string;
        count: number;
    }[];
    recentItems: SpendDashboardItem[];
    categories: SpendDashboardCategory[];
    totalSpendItemsCalculated: number;
};

export type SpendCategoryVendor = {
    id: number;
    name: string;
    itemCount: number;
    awardedItemCount: number;
    totalSpend: SpendMoney;
};

export type SpendCategoryPage = {
    id: number;
    name: string;
    colorHex: string;
    summary: {
        totalSpend: SpendMoney;
        totalRfqItems: number;
        avgSavingsPerItem: SpendMoney | null;
        vendorCount: number;
    };
    items: SpendDashboardItem[];
    vendors: SpendCategoryVendor[];
};

export const SpendItemUpdateSchema = z.object({
    items: z.array(
        z.object({
            itemId: z.number(),
            moveToCategoryId: z.number().nullable().optional(),
            assignProductId: z.number().optional(),
        }),
    ),
});

/**
 *
 * End sync with backend
 *
 */

export type SpendCategoriesResponse = {
    categories: SpendCategoryListing[];
};

export type SpendCategoryPayload = {
    name: string;
    colorHex?: string;
};

export type DeleteSpendCategoryParams = {
    categoryId: number;
    moveToCategoryId?: number | null;
};

export type SpendDashboardParams = {
    startDate: number;
    endDate: number;
};

type SpendItemUpdate = z.infer<typeof SpendItemUpdateSchema>;

export default {
    getSpendDashboard: (params: SpendDashboardParams): Promise<SpendDashboard> => {
        const searchParams = new URLSearchParams({
            startDate: String(params.startDate),
            endDate: String(params.endDate),
        });

        return fetch(`${PURCHASER_API_BASE_URL}/spend?${searchParams.toString()}`, {
            headers: getHeaders(),
            method: "GET",
        }).then(res => res.json());
    },
    getSpendCategoryPage: (categoryId: number): Promise<SpendCategoryPage> =>
        fetch(`${PURCHASER_API_BASE_URL}/spend/categories/${categoryId}`, {
            headers: getHeaders(),
            method: "GET",
        }).then(res => res.json()),
    listSpendCategories: (): Promise<SpendCategoriesResponse> =>
        fetch(`${PURCHASER_API_BASE_URL}/spend/categories`, {
            headers: getHeaders(),
            method: "GET",
        }).then(res => res.json()),
    createSpendCategory: (payload: SpendCategoryPayload): Promise<SpendDashboardCategory> =>
        fetch(`${PURCHASER_API_BASE_URL}/spend/categories`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify(payload),
        }).then(res => res.json()),
    updateSpendCategory: (categoryId: number, payload: Partial<SpendCategoryPayload>) =>
        fetch(`${PURCHASER_API_BASE_URL}/spend/categories/${categoryId}`, {
            headers: getHeaders(),
            method: "PATCH",
            body: JSON.stringify(payload),
        }).then(res => res.json()),
    deleteSpendCategory: (params: DeleteSpendCategoryParams): Promise<{ success: true }> =>
        fetch(`${PURCHASER_API_BASE_URL}/spend/categories/${params.categoryId}`, {
            headers: getHeaders(),
            method: "DELETE",
            body: JSON.stringify({ moveToCategoryId: params.moveToCategoryId ?? null }),
        }).then(res => res.json()),
    updateSpendItems: (params: SpendItemUpdate): Promise<{ success: true }> =>
        fetch(`${PURCHASER_API_BASE_URL}/spend/items`, {
            headers: getHeaders(),
            method: "PATCH",
            body: JSON.stringify(params),
        }).then(res => res.json()),
};
